'use client';

/**
 * useMyFeatures — server-resolved feature entitlements for the current user.
 *
 * Backed by `GET /api/v1/users/me/features`. Unlike the plan-name-based
 * `canUse` mirror, this reflects the backend's account-shape model exactly:
 *
 *   - solo orgs (`plan_type="user"`) + company/super admins → `unrestricted`
 *     (every active feature key is returned);
 *   - a corporate/enterprise **member** → only the keys their admin granted
 *     via `org_user_features` (default-deny).
 *
 * The FE uses this to show/hide gated modules so a member never sees a module
 * they aren't entitled to (and never eats a surprise 402 on click). Lives in
 * TanStack Query alongside `useUsersMe`, disabled while signed out.
 */
import { useQuery } from '@tanstack/react-query';

import { AuthError, NetworkError, useApi } from '@/lib/api';

import type { PlanType, Role } from './types';
import { useAuthToken } from './useAuthToken';

export const MY_FEATURES_QUERY_KEY = ['auth', 'me', 'features'] as const;

export interface MyFeatures {
  /** Feature keys the current user may use. */
  features: string[];
  /** True for solo orgs + company/super admins — they hold every feature. */
  unrestricted: boolean;
  role: Role;
  plan_type: PlanType | null;
}

export function useMyFeatures() {
  const api = useApi();
  const token = useAuthToken();
  return useQuery({
    queryKey: MY_FEATURES_QUERY_KEY,
    queryFn: ({ signal }) => api<MyFeatures>('/users/me/features', { signal }),
    staleTime: 30_000,
    enabled: token !== null,
    retry: (failureCount, error) => {
      if (error instanceof AuthError) return false;
      if (error instanceof NetworkError) return failureCount < 1;
      return false;
    },
  });
}
